import React, {
    useEffect, useMemo, useState, useContext
} from 'react';
import { LocaleService, TranslatorContext } from '@jutro/locale';
import { Image } from '@jutro/components';
import { UAParser } from 'ua-parser-js';
import styles from './UnsupportedBrowserBannerStyles.module.scss';
import {
    COMPATIBLEBROWSERWIN,
    COMPATIBLEBROWSERMAC,
    COMPATIBLEBROWSERANDRIODMOBILE,
    COMPATIBLEBROWSERIOSMOBILE,
    COMPATIBLEBROWSERANDRIODTAB,
    COMPATIBLEBROWSERIPAD,
    DEVICETYPE,
    SUPPORTEDBROWSERLIST,
    BROWSERLIST,
} from '../../../constants/constants';
import messages from './UnsupportedBrowserBanner.messages';

export default function UnsupportedBrowserBanner() {
    const translator = useContext(TranslatorContext);
    const [showBanner, setShowBanner] = useState(false);

    // Parse UA once
    const parser = useMemo(() => new UAParser(), []);
    const browserDetails = useMemo(() => parser.getResult(), [parser]);

    const rawBrowserName = browserDetails?.browser?.name || '';
    const browserVersion = parseInt(browserDetails?.browser?.major ?? '0', 10);
    const deviceType = browserDetails?.device?.type || DEVICETYPE.DESKTOP;
    const osName = browserDetails?.os?.name || '';

    const isFrench = LocaleService.currentLanguage === "fr"; 

    useEffect(() => {
        const normalizedBrowserName = Object.values(SUPPORTEDBROWSERLIST).find(
            (supported) => rawBrowserName?.includes(supported)
        )
        ?? rawBrowserName;

        let compatibilityMap;

        if (deviceType === DEVICETYPE.MOBILE) {
            if (osName === 'Android') {
                compatibilityMap = COMPATIBLEBROWSERANDRIODMOBILE;
            } else if (osName === 'iOS') {
                compatibilityMap = COMPATIBLEBROWSERIOSMOBILE;
            }
        } else if (deviceType === DEVICETYPE.TABLET) {
            if (osName === 'Android') {
                compatibilityMap = COMPATIBLEBROWSERANDRIODTAB;
            } else if (osName === 'iOS') {
                compatibilityMap = COMPATIBLEBROWSERIPAD;
            }
        } else if (deviceType === DEVICETYPE.DESKTOP) {
            if (osName === 'Windows') {
                compatibilityMap = COMPATIBLEBROWSERWIN;
            } else if (osName === 'macOS') {
                compatibilityMap = COMPATIBLEBROWSERMAC;
            }
        }

        const hasMap = !!compatibilityMap;
        const hasBrowserName = !!normalizedBrowserName;

        const isBrowserSupportedOnDevice = hasMap && hasBrowserName
            ? Object.prototype.hasOwnProperty.call(
                compatibilityMap,
                normalizedBrowserName
            )
            : false;

        const minSupportedVersion = isBrowserSupportedOnDevice
            ? compatibilityMap[normalizedBrowserName]
            : undefined;

        const shouldShowBanner = (hasMap && hasBrowserName && !isBrowserSupportedOnDevice)
        || (minSupportedVersion !== undefined
        && browserVersion < minSupportedVersion);

        setShowBanner(shouldShowBanner);
    }, [rawBrowserName, browserVersion, deviceType, osName]);

    const onCrossClick = () => {
        setShowBanner(false);
    };

    if (!showBanner) return null;

     return showBanner ? (
        <div className={styles.banner}>
            <div className={styles.bannerMidContainer}>
                <div className={styles.bannerInnerDiv}>
                    <div className={styles.bannerLeftInfo}>
                        <div
                            className={styles.bannerLeftInfoDiv}
                            aria-label={translator(messages.warning)}
                        >
                            <Image
                                src='/branding/exclamation-triangle-solid.svg'
                                className={styles.bannerLeftInfoImg}
                                showLoader={false}
                                alt={translator(messages.warning)}
                                aria-hidden="true"
                            />
                        </div>
                        <p>{translator(messages.unsopportedBrowserMessage)}</p>
                    </div>
                    <div
                        className={styles.closingIcon}
                        aria-label={translator(messages.closeIcon)}
                        tabIndex={0}
                        role="button"
                        onClick={() => onCrossClick()}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onCrossClick();
                            }
                        }}
                    >
                        <Image
                            src='branding/times-solid_black.svg'
                            className={styles.closingIconStyle}
                            showLoader={false}
                            data-testid="crossIcon"
                            alt={translator(messages.closeIcon)}
                            aria-hidden="true"
                        />
                    </div>
                </div>
                <div className={styles.browseIcons}>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <div className={styles.browseIconsDiv} tabIndex={0}>
                        <a
                            href={
                                isFrench
                                    ? BROWSERLIST.CHROME_FR
                                    : BROWSERLIST.CHROME
                            }
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`${translator(messages.chromeBrowser)} ${translator(messages.opensInNewTab)}`}
                            tabIndex={-1}
                        >
                            <div className={styles.browseIconsDivImage}>
                                <Image
                                    src='/branding/Google_Chrome_icon.svg'
                                    className={`${styles.cursorPointer} ${styles.browseIconsImgStyle}`}
                                    showLoader={false}
                                    alt={translator(messages.chromeBrowser)}
                                    aria-hidden="true"
                                />
                            </div>
                            <div>
                                <span>
                                    {translator(messages.chromeBrowser)}
                                </span>
                            </div>
                        </a>
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <div className={styles.browseIconsDiv} tabIndex={0}>
                        <a
                            href={
                                isFrench
                                    ? BROWSERLIST.EDGE_FR
                                    : BROWSERLIST.EDGE
                            }
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`${translator(messages.edgeBrowser)} ${translator(messages.opensInNewTab)}`}
                            tabIndex={-1}
                        >
                            <div className={styles.browseIconsDivImage}>
                                <Image
                                    src='/branding/Microsoft_Edge_logo.svg'
                                    className={`${styles.cursorPointer} ${styles.browseIconsImgStyle}`}
                                    showLoader={false}
                                    alt={translator(messages.edgeBrowser)}
                                    aria-hidden="true"
                                />
                            </div>
                            <div>
                                <span>{translator(messages.edgeBrowser)}</span>
                            </div>
                        </a>
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <div className={styles.browseIconsDiv} tabIndex={0}>
                        <a
                            href={
                                isFrench
                                    ? BROWSERLIST.FIREFOX_FR
                                    : BROWSERLIST.FIREFOX
                            }
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`${translator(messages.firefoxBrowser)} ${translator(messages.opensInNewTab)}`}
                            tabIndex={-1}
                        >
                            <div className={styles.browseIconsDivImage}>
                                <Image
                                    src='/branding/Firefox_logo.svg'
                                    className={`${styles.cursorPointer} ${styles.browseIconsImgStyle}`}
                                    showLoader={false}
                                    alt={translator(messages.firefoxBrowser)}
                                    aria-hidden="true"
                                />
                            </div>
                            <div>
                                <span>
                                    {translator(messages.firefoxBrowser)}
                                </span>
                            </div>
                        </a>
                    </div>
                    {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                    <div className={styles.browseIconsDiv} tabIndex={0}>
                        <a
                            href={
                                isFrench
                                    ? BROWSERLIST.SAFARI_FR
                                    : BROWSERLIST.SAFARI
                            }
                            target="_blank"
                            rel="noreferrer"
                            aria-label={`${translator(messages.safariBrowser)} ${translator(messages.opensInNewTab)}`}
                            tabIndex={-1}
                        >
                            <div className={styles.browseIconsDivImage}>
                                <Image
                                    src='/branding/safari-icon.svg'
                                    className={`${styles.cursorPointer} ${styles.browseIconsImgStyle}`}
                                    showLoader={false}
                                    alt={translator(messages.safariBrowser)}
                                    aria-hidden="true"
                                />
                            </div>
                            <div>
                                <span>
                                    {translator(messages.safariBrowser)}
                                </span>
                            </div>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    ) : null;
}
 